import React, { useEffect, useRef, useState } from 'react';
import { AspectRatio, CaptionAlign, KenBurnsEffect, KenBurnsStep, LibraryAsset, SlideState, TextStyleFields } from './types';
import ContentTab from './tabs/ContentTab';
import MotionTab from './tabs/MotionTab';
import AudioTab from './tabs/AudioTab';
import StoryTab from './tabs/StoryTab';

export type PanelTab = 'content' | 'motion' | 'audio' | 'story' | 'renders';

const TABS: { value: PanelTab; label: string; icon: string }[] = [
  { value: 'content', label: 'Content', icon: '✍️' },
  { value: 'motion', label: 'Motion', icon: '🎞' },
  { value: 'audio', label: 'Audio', icon: '🎵' },
  { value: 'story', label: 'Story', icon: '📽' },
  { value: 'renders', label: 'Renders', icon: '🎬' },
];

export interface RightPanelProps {
  /** The Renders tab's content, owned by the page so it can play a render in the preview. */
  rendersTab?: React.ReactNode;
  /** True once the story has at least one render, which decides the tab opened first. */
  hasRenders?: boolean;
  slide: SlideState;
  slideIndex: number;
  slideCount: number;
  durationIsFixed: boolean;
  enabledKBSteps: KenBurnsStep[];
  totalKBDuration: number;
  audioLibrary: LibraryAsset[];
  loadingLibrary: boolean;

  updateActive: (patch: Partial<SlideState>) => void;
  setCaptionText: (text: string) => void;
  setCaptionAlign: (align: CaptionAlign) => void;
  setCaptionStyle: (patch: Partial<TextStyleFields>) => void;
  clearCaption: () => void;
  updateNarrationText: (text: string) => void;
  setNarrationAlign: (align: CaptionAlign) => void;
  setNarrationStyle: (patch: Partial<TextStyleFields>) => void;
  toggleKBEffect: (effect: KenBurnsEffect) => void;
  updateKBDuration: (effect: KenBurnsEffect, duration: number) => void;
  addLibraryAudio: (asset: LibraryAsset, mode: 'foreground' | 'background') => void;
  removeAudio: (audioIdx: number) => void;
  onUploadForeground: () => void;
  onUploadBackground: () => void;
  onOpenClipEditor: () => void;
  openingClip?: boolean;

  description: string;
  onDescriptionChange: (value: string) => void;
  aspectRatio: AspectRatio;
  onAspectRatioChange: (ratio: AspectRatio) => void;
  platform: string;
  onPlatformChange: (value: string) => void;
  audience: string;
  onAudienceChange: (value: string) => void;
}

/**
 * The Story editor's right panel. Content, Motion and Audio are slide-scoped and branch on
 * whether the slide is an image or a clip; Story is project-scoped and the same throughout.
 */
const RightPanel: React.FC<RightPanelProps> = props => {
  // Renders open first when the story has one to show — an author coming back to a finished
  // story is here to watch it, not to keep editing. Only until they pick another tab.
  const [tab, setTab] = useState<PanelTab>(props.hasRenders ? 'renders' : 'content');
  const openedOnRenders = useRef(props.hasRenders);
  useEffect(() => {
    if (props.hasRenders && !openedOnRenders.current) {
      openedOnRenders.current = true;
      setTab('renders');
    }
  }, [props.hasRenders]);

  const isClip = props.slide.sourceType === 'video';

  return (
    <div className="se-right-panel">
      <div className="se-tabs" role="tablist">
        {TABS.map(t => (
          <button
            key={t.value}
            role="tab"
            aria-selected={tab === t.value}
            className={`se-tab ${tab === t.value ? 'se-tab-active' : ''}`}
            onClick={() => setTab(t.value)}
          >
            <span className="se-tab-icon">{t.icon}</span>
            <span className="se-tab-label">{t.label}</span>
          </button>
        ))}
      </div>

      <div className="se-tab-body">
        {tab === 'renders' && (props.rendersTab ?? (
          <div className="kc-renders-empty">No renders yet. Click Generate Story.</div>
        ))}
        {tab === 'content' && (
          <ContentTab
            slide={props.slide}
            isClip={isClip}
            onOpenClipEditor={props.onOpenClipEditor}
            openingClip={props.openingClip}
            updateActive={props.updateActive}
            setCaptionText={props.setCaptionText}
            setCaptionAlign={props.setCaptionAlign}
            setCaptionStyle={props.setCaptionStyle}
            clearCaption={props.clearCaption}
            updateNarrationText={props.updateNarrationText}
            setNarrationAlign={props.setNarrationAlign}
            setNarrationStyle={props.setNarrationStyle}
          />
        )}
        {tab === 'motion' && (
          <MotionTab
            slide={props.slide}
            isClip={isClip}
            slideIndex={props.slideIndex}
            durationIsFixed={props.durationIsFixed}
            enabledKBSteps={props.enabledKBSteps}
            totalKBDuration={props.totalKBDuration}
            updateActive={props.updateActive}
            toggleKBEffect={props.toggleKBEffect}
            updateKBDuration={props.updateKBDuration}
          />
        )}
        {tab === 'audio' && (
          <AudioTab
            slide={props.slide}
            isClip={isClip}
            audioLibrary={props.audioLibrary}
            loadingLibrary={props.loadingLibrary}
            updateActive={props.updateActive}
            addLibraryAudio={props.addLibraryAudio}
            removeAudio={props.removeAudio}
            onUploadForeground={props.onUploadForeground}
            onUploadBackground={props.onUploadBackground}
          />
        )}
        {tab === 'story' && (
          <StoryTab
            description={props.description}
            onDescriptionChange={props.onDescriptionChange}
            aspectRatio={props.aspectRatio}
            onAspectRatioChange={props.onAspectRatioChange}
            platform={props.platform}
            onPlatformChange={props.onPlatformChange}
            audience={props.audience}
            onAudienceChange={props.onAudienceChange}
            slideCount={props.slideCount}
          />
        )}
      </div>
    </div>
  );
};

export default RightPanel;
